import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Users, TrendingUp, Award, ScatterChart } from 'lucide-react';
import { motion } from 'framer-motion';

import { API_BASE } from '../config';

// const API_BASE = "http://127.0.0.1:8000";

export default function Insights() {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchStats = async () => {
            try {
                const res = await axios.get(`${API_BASE}/stats`);
                setStats(res.data);
            } catch (err) {
                console.error("Failed to load stats", err);
            } finally {
                setLoading(false);
            }
        };
        fetchStats();
    }, []);

    if (loading) return <div className="p-20 text-center"><div className="animate-spin w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full mx-auto mb-4"></div><p className="text-slate-500">Analysing talent pool...</p></div>;

    if (!stats) return <div className="p-10 text-center text-red-500">Failed to load insights.</div>;

    // Process Skills
    const topSkills = Object.entries(stats.skills_distribution || {})
        .sort(([, a], [, b]) => b - a)
        .slice(0, 6);
    const maxSkillCount = Math.max(...topSkills.map(([, c]) => c), 1);

    // Process Experience (Donut Data)
    const expDist = Object.entries(stats.experience_distribution || {});
    const totalExp = expDist.reduce((acc, [, val]) => acc + val, 0);

    // Process Trend Analysis
    const { points, slope, intercept, correlation } = stats.trend_analysis || { points: [], slope: 0, intercept: 0 };

    return (
        <div className="space-y-8 animate-fade-in pb-10">
            <div className="flex justify-between items-end">
                <div>
                    <h2 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-brand-700 to-purple-600 dark:from-brand-400 dark:to-purple-300">
                        Talent Intelligence
                    </h2>
                    <p className="text-slate-500 dark:text-slate-400 mt-1">AI-driven analytics & regression models</p>
                </div>
                <div className="text-right hidden sm:block">
                    <p className="text-xs font-semibold uppercase text-slate-400 tracking-wider">Models Trained</p>
                    <p className="text-lg font-bold text-slate-800 dark:text-slate-100 font-mono">ON-THE-FLY</p>
                </div>
            </div>

            {/* Key Metrics Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <MetricCard label="Candidates" value={stats.total_resumes} subvalue="Active Profiles" icon={Users} color="brand" />
                <MetricCard label="Avg Experience" value={stats.avg_years_experience} suffix=" Years" subvalue="Seniority Mean" icon={TrendingUp} color="green" />
                <MetricCard label="Top Skill" value={topSkills[0]?.[0] || "N/A"} subvalue="Dominant Tech" icon={Award} color="purple" />
                <MetricCard label="Correlation" value={(correlation || 0).toFixed(2)} subvalue="Exp vs Skill Count" icon={ScatterChart} color="amber" />
            </div>

            {/* Charts Section */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">

                {/* 1. Skills Chart */}
                <div className="bg-white dark:bg-slate-800 p-7 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-700 flex flex-col h-[400px]">
                    <div className="mb-6">
                        <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100">Top Skills</h3>
                        <p className="text-sm text-slate-500 dark:text-slate-400">Frequency distribution</p>
                    </div>
                    <div className="space-y-4 flex-1 overflow-y-auto pr-2 custom-scrollbar">
                        {topSkills.map(([skill, count], i) => (
                            <div key={skill} className="group">
                                <div className="flex justify-between text-sm mb-1.5">
                                    <span className="font-medium text-slate-700 dark:text-slate-200">{skill}</span>
                                    <span className="text-slate-400 text-xs">{count}</span>
                                </div>
                                <div className="h-2 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                                    <motion.div
                                        initial={{ width: 0 }}
                                        animate={{ width: `${(count / maxSkillCount) * 100}%` }}
                                        transition={{ duration: 1, delay: i * 0.1 }}
                                        className="h-full bg-gradient-to-r from-brand-500 to-purple-500 rounded-full relative"
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* 2. Experience Donut */}
                <div className="bg-white dark:bg-slate-800 p-7 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-700 flex flex-col h-[400px]">
                    <div className="mb-2">
                        <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100">Seniority Split</h3>
                        <p className="text-sm text-slate-500 dark:text-slate-400">Experience levels breakdown</p>
                    </div>

                    <div className="flex-1 flex items-center justify-center relative">
                        {/* Custom SVG Donut */}
                        <CustomDonutChart data={expDist} total={totalExp} />
                    </div>
                </div>

                {/* 3. ML Scatter Plot */}
                <div className="bg-white dark:bg-slate-800 p-7 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-700 flex flex-col col-span-1 lg:col-span-2 h-[450px]">
                    <div className="mb-6 flex justify-between items-start">
                        <div>
                            <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2">
                                <span className="bg-brand-100 text-brand-700 text-xs px-2 py-0.5 rounded border border-brand-200 dark:bg-brand-900/30 dark:text-brand-300 dark:border-brand-800">BETA</span>
                                Skill Density Analysis
                            </h3>
                            <p className="text-sm text-slate-500 dark:text-slate-400">Linear regression: Years of Experience vs Number of Skills</p>
                        </div>
                        <div className="text-right text-xs">
                            <span className="font-bold block text-slate-700 dark:text-slate-300">r = {(correlation || 0).toFixed(2)}</span>
                            <span className="text-slate-400">Correlation Coeff</span>
                        </div>
                    </div>

                    <div className="flex-1 w-full bg-slate-50 dark:bg-slate-900/50 rounded-xl border border-slate-100 dark:border-slate-800 relative overflow-hidden p-4">
                        <CustomScatterPlot points={points} slope={slope} intercept={intercept} />
                    </div>
                    <p className="text-xs text-center text-slate-400 mt-3 italic">
                        {correlation > 0.3 ? "📈 Insight: More experienced candidates tend to list more skills." :
                            correlation < -0.3 ? "📉 Insight: Senior profiles are more specialized with fewer listed skills." :
                                "➖ Insight: No strong correlation between experience and skill count."}
                    </p>
                </div>
            </div>
        </div>
    );
}

function CustomDonutChart({ data, total }) {
    if (total === 0) return <div className="text-slate-400 text-sm">No data available</div>;

    let cumulativePercent = 0;
    const colors = ['#6366f1', '#10b981', '#f59e0b', '#8b5cf6']; // indigo, emerald, amber, violet

    return (
        <div className="relative w-64 h-64">

            <svg viewBox="0 0 100 100" className="transform -rotate-90 w-full h-full">
                {data.map(([label, count], i) => {
                    const percent = count / total;
                    const strokeDasharray = `${percent * 100} 100`;
                    const strokeDashoffset = -cumulativePercent * 100;
                    cumulativePercent += percent;

                    return (
                        <motion.circle
                            key={label}
                            initial={{ strokeDasharray: "0 100" }}
                            animate={{ strokeDasharray: strokeDasharray }}
                            transition={{ duration: 1, delay: i * 0.2 }}
                            cx="50" cy="50" r="40"
                            fill="none"
                            stroke={colors[i % colors.length]}
                            strokeWidth="12"
                            strokeDasharray={strokeDasharray}
                            strokeDashoffset={strokeDashoffset}
                            className="hover:opacity-80 transition-opacity cursor-pointer group"
                        >
                            <title>{label}: {count} candidates</title>
                        </motion.circle>
                    );
                })}
            </svg>
            {/* Legend overlay or Center Text */}
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <span className="text-3xl font-bold text-slate-800 dark:text-white">{total}</span>
                <span className="text-xs text-slate-500">Total</span>
            </div>

            {/* Legend */}
            <div className="absolute -bottom-10 left-0 right-0 flex justify-center gap-3 flex-wrap">
                {data.map(([label, count], i) => (
                    <div key={label} className="flex items-center gap-1.5">
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: colors[i % colors.length] }} />
                        <span className="text-[10px] text-slate-500 dark:text-slate-400">{label} ({Math.round(count / total * 100)}%)</span>
                    </div>
                ))}
            </div>
        </div>
    );
}

function CustomScatterPlot({ points, slope, intercept }) {
    if (!points || points.length === 0) return <div className="flex items-center justify-center h-full text-slate-400">No data points</div>;

    // Scaling logic
    const maxX = Math.max(...points.map(p => p.x), 10) + 2;
    const maxY = Math.max(...points.map(p => p.y), 10) + 5;

    // Return percentages directly
    const getX = (x) => (x / maxX) * 100;
    const getY = (y) => 100 - (y / maxY) * 100;

    // Generate Regression Line points
    const x1 = 0;
    const y1 = intercept;
    const x2 = maxX;
    const y2 = slope * maxX + intercept;

    return (
        <div className="w-full h-full relative">
            <svg className="w-full h-full overflow-visible">
                {/* Axis Lines */}
                <line x1="0" y1="100%" x2="100%" y2="100%" stroke="#cbd5e1" strokeWidth="1" />
                <line x1="0" y1="0" x2="0" y2="100%" stroke="#cbd5e1" strokeWidth="1" />

                {/* Points */}
                {points.map((p, i) => (
                    <motion.circle
                        key={i}
                        initial={{ opacity: 0, scale: 0 }}
                        animate={{ opacity: 0.6, scale: 1 }}
                        transition={{ delay: i * 0.02 }}
                        cx={`${getX(p.x)}%`}
                        cy={`${getY(p.y)}%`}
                        r="4" // Fixed pixel radius
                        className="fill-brand-500 dark:fill-brand-400 hover:fill-brand-700 cursor-pointer"
                    >
                        <title>{p.x}y Exp, {p.y} Skills</title>
                    </motion.circle>
                ))}

                {/* Regression Line */}
                <motion.line
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ duration: 1.5, delay: 0.5 }}
                    x1={`${getX(x1)}%`}
                    y1={`${getY(y1)}%`}
                    x2={`${getX(x2)}%`}
                    y2={`${getY(y2)}%`}
                    stroke="#ec4899" // Pink line for contrast
                    strokeWidth="2"
                    strokeDasharray="4 4"
                />
            </svg>
            <div className="absolute bottom-2 right-2 text-[10px] text-slate-400">X: Experience (Years)</div>
            <div className="absolute top-2 left-2 text-[10px] text-slate-400">Y: Skills Count</div>
        </div>
    );
}

function MetricCard({ label, value, subvalue, icon: Icon, color, suffix = '' }) {
    const theme = {
        brand: 'bg-brand-50 text-brand-600 dark:bg-brand-900/20 dark:text-brand-400',
        green: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-900/20 dark:text-emerald-400',
        purple: 'bg-violet-50 text-violet-600 dark:bg-violet-900/20 dark:text-violet-400',
        amber: 'bg-amber-50 text-amber-600 dark:bg-amber-900/20 dark:text-amber-400',
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white dark:bg-slate-800 p-6 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-700 relative overflow-hidden group hover:shadow-md transition-shadow"
        >
            <div className="relative z-10">
                <div className="flex justify-between items-start mb-4">
                    <div className={`p-3 rounded-xl ${theme[color]} transition-transform group-hover:scale-110`}>
                        <Icon className="w-6 h-6" />
                    </div>
                </div>
                <div>
                    <h4 className="text-3xl font-bold text-slate-800 dark:text-white mb-1">
                        {value}<span className="text-lg text-slate-400 font-normal ml-0.5">{suffix}</span>
                    </h4>
                    <p className="text-sm font-medium text-slate-500 dark:text-slate-400">{label}</p>
                    <p className="text-xs text-slate-400 mt-2">{subvalue}</p>
                </div>
            </div>

            {/* Decoration */}
            <div className={`absolute -right-6 -bottom-6 w-24 h-24 rounded-full ${theme[color]} opacity-20 blur-2xl group-hover:opacity-30 transition-opacity`} />
        </motion.div>
    );
}
